import { createContext, useMemo, useContext, useState } from 'react';
import cx from 'classnames';
import { useIsKeyboardNavigationState } from '../../hooks/useKeyboardNavigation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const BoxSelectorContext = /*#__PURE__*/createContext(undefined);
function BoxSelector(_ref) {
  let {
    onChange,
    onBlur = () => {},
    onFocus = () => {},
    className,
    name,
    value,
    disabled,
    children,
    mktSelector,
    type = 'radio',
    isValid,
    columnClassName: _columnClassName,
    ...wrapperProps
  } = _ref;
  const providerData = useMemo(() => {
    return {
      name,
      value,
      type,
      onFocus,
      onBlur,
      onChange,
      disabled,
      isValid: isValid !== false
    };
  }, [name, value, type, onFocus, onBlur, onChange, disabled, isValid]);
  return /*#__PURE__*/_jsx(BoxSelectorContext.Provider, {
    value: providerData,
    children: /*#__PURE__*/_jsx("div", {
      className: cx('box-selector', 'row', className),
      "data-mkt-selector": mktSelector,
      ...wrapperProps,
      children: children
    })
  });
}
function BoxColumn(_ref2) {
  let {
    className = 'col',
    children
  } = _ref2;
  return /*#__PURE__*/_jsx("div", {
    className: cx('box-selector__column', className),
    children: children
  });
}
function BoxItem(_ref3) {
  let {
    children,
    value,
    className,
    selectedClassName = '',
    disabledClassName = '',
    mktSelector,
    qaSelector
  } = _ref3;
  const {
    id,
    isSelected,
    disabled,
    selectedValue,
    name,
    type,
    onChange,
    classNames,
    onFocus: onFocusHandler,
    onBlur: onBlurHander
  } = useBoxSelectorItem({
    value,
    className,
    selectedClassName,
    disabledClassName
  });
  const usableValue = typeof value === 'boolean' ? String(value) : value;
  return /*#__PURE__*/_jsxs("label", {
    htmlFor: id
    // @ts-expect-error  TODO: fix types
    ,
    disabled: disabled,
    className: classNames,
    "data-qa-value": value,
    "data-mkt-selector": mktSelector,
    "data-qa-selector": qaSelector,
    children: [typeof children === 'function' ?
    //@ts-expect-error
    children({
      isSelected,
      selectedValue
    }) : children, /*#__PURE__*/_jsx("input", {
      className: "visually-hidden",
      id: id,
      type: type,
      name: name,
      value: usableValue
      //@ts-expect-error
      ,
      checked: isSelected,
      disabled: disabled,
      onChange: e => {
        onChange(value, e);
      },
      onBlur: onBlurHander,
      onFocus: onFocusHandler
    })]
  });
}
function useBoxSelectorItem(props) {
  const context = useContext(BoxSelectorContext);
  const isTabbing = useIsKeyboardNavigationState();
  const [hasFocus, setHasFocus] = useState(false);
  if (context === undefined) {
    throw new Error('useBoxSelectorItem must be used within a BoxSelector element');
  }
  const {
    name,
    value: selectedValue,
    type,
    onBlur,
    onFocus,
    disabled,
    isValid
  } = context;
  const {
    value,
    className,
    selectedClassName = '',
    disabledClassName = ''
  } = props;
  const hasFocusRing = isTabbing && hasFocus;
  const id = name + "_" + value;
  const isSelected = type === 'checkbox' ? Array.isArray(selectedValue) ? selectedValue.includes(value) : selectedValue : value === selectedValue;
  const classNames = cx('box-selector__item', className, {
    'box-selector__item--is-selected': isSelected,
    'box-selector__item--is-disabled': disabled,
    'box-selector__item--has-ring': hasFocusRing,
    'box-selector__item--has-error': !isValid,
    [selectedClassName]: isSelected,
    [disabledClassName]: disabled
  });
  function onBlurHander(e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    setHasFocus(false);
  }
  function onFocusHandler(e) {
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
    setHasFocus(true);
  }
  return {
    ...context,
    id,
    isSelected,
    selectedValue,
    name,
    value: props.value,
    onBlur: onBlurHander,
    onFocus: onFocusHandler,
    hasFocus: hasFocus,
    hasFocusRing,
    classNames,
    isValid
  };
}
export default BoxSelector;
export { BoxItem, BoxColumn, BoxSelector };